import { useTranslations } from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { AccessApiKeyActions } from "./AccessApiKeyActions";
import { AccessApiKeyIdentity } from "./AccessApiKeyIdentity";
import { type AccessApiKeysListProps, canOpenKeyRolesFor } from "./accessApiKeysList.types";
import { AccessEffectiveCell } from "./AccessEffectiveCell";
import { PrincipalRolesCell } from "./PrincipalRolesCell";

/**
 * Three fixed tracks, so the grid cannot shrink below 442px, inside an `overflow-hidden` wrapper that does
 * not truncate what exceeds it — it clips the last columns out of existence. Below the breakpoint the same
 * keys are painted by `AccessApiKeysCards`.
 *
 * The breakpoint is `xl`, not `lg`, and the difference is not cosmetic. `lg` means a 1024px VIEWPORT, but
 * this page concedes far less than that: the dashboard sidebar takes 256px and the settings sub-nav another
 * 220 before any padding, so the grid only has room from about 1072px up. Between 1024 and 1071 the row lost
 * 48px — enough to delete the whole `⋯` menu, which is the only way to disable a key or edit its IPs. Pick
 * the breakpoint against the width the page actually grants, never against the viewport.
 */
const GRID_COLS = "grid grid-cols-[1fr_210px_120px_112px] items-center";

/** Desktop face of the API keys list. Mobile is `AccessApiKeysCards`; both take `AccessApiKeysListProps`. */
export function AccessApiKeysTable({
  keys,
  rolesByKey,
  rolesUnavailable,
  summaryById,
  catalogUnavailable,
  canManageRoles,
  canUpdate,
  lastUsedLabel,
  onOpenRoles,
  onEditIps,
  onDisable,
  onEnable,
}: AccessApiKeysListProps) {
  const { t } = useTranslations("settings");

  return (
    <div className="overflow-hidden rounded-md border border-border">
      <div
        className={cn(
          GRID_COLS,
          "border-b border-border bg-muted/40 px-3.5 py-2.5 font-mono text-[10.5px] uppercase tracking-[0.06em] text-muted-foreground",
        )}
      >
        <div>{t("access_col_key")}</div>
        <div>{t("access_col_roles")}</div>
        <div>{t("access_col_effective")}</div>
        {/* Named for assistive tech even though it is visually blank. */}
        <span className="sr-only">{t("access_col_actions")}</span>
      </div>

      {keys.length === 0 ? (
        <div className="px-3.5 py-8 text-center text-[13px] text-muted-foreground">
          {t("access_no_results")}
        </div>
      ) : (
        keys.map((k, i) => {
          const assigned = rolesByKey[k.id] ?? [];
          const rolesFailed = rolesUnavailable.has(k.id);
          const openRolesDrawer = canOpenKeyRolesFor(k.id, catalogUnavailable, rolesUnavailable)
            ? () => onOpenRoles(k)
            : undefined;

          return (
            <div
              key={k.id}
              className={cn(
                GRID_COLS,
                "px-3.5 py-3",
                i !== keys.length - 1 && "border-b border-border",
                !k.is_active && "opacity-55",
              )}
            >
              <div className="pr-3">
                <AccessApiKeyIdentity apiKey={k} lastUsedLabel={lastUsedLabel(k)} />
              </div>

              {/* Roles — pills capped; "+N" opens the same drawer as the row's action button */}
              <PrincipalRolesCell roles={assigned} rolesFailed={rolesFailed} onShowAll={openRolesDrawer} />

              <div>
                <AccessEffectiveCell
                  summary={summaryById[k.id]}
                  noRoles={!rolesFailed && assigned.length === 0}
                  rolesFailed={rolesFailed}
                  catalogUnavailable={catalogUnavailable}
                />
              </div>

              <AccessApiKeyActions
                apiKey={k}
                onOpenRoles={openRolesDrawer}
                canManageRoles={canManageRoles}
                canUpdate={canUpdate}
                onEditIps={onEditIps}
                onDisable={onDisable}
                onEnable={onEnable}
              />
            </div>
          );
        })
      )}
    </div>
  );
}
